import { useEffect, useRef } from "react"

import {
  ASYNC_TASK_POLL_INTERVAL_MS,
  useQueryAsyncTask,
} from "api/reactQuery/queries/tasks"
import { STATUS } from "api/reactQuery/queries/tasks.types"
import { AxiosError } from "axios"
import { useAppDispatch, useAppSelector } from "hooks/store"
import {
  selectResetBalanceTaskData,
  selectResetBalanceTaskId,
} from "store/utility/utility.selectors"
import {
  setResetBalanceTaskData,
  setResetBalanceTaskId,
} from "store/utility/utility.slice"
import {
  clearPersistedResetBalanceTaskId,
  getPersistedResetBalanceTaskId,
  isPersistedResetBalanceTaskStale,
} from "utils/resetBalanceTask"

import {
  getResetBalanceStaleOutcome,
  getResetBalanceTaskOutcome,
  IResetBalanceTaskOutcome,
  isResetBalanceTaskInFlight,
} from "./getResetBalanceTaskOutcome"

interface IUseResetBalanceTaskArgs {
  classId: string | undefined
  onOutcome: (outcome: IResetBalanceTaskOutcome) => void
  onPollError: (err: AxiosError) => void
}

interface IUseResetBalanceTaskResult {
  isProcessingBalance: boolean
  status: STATUS | undefined
  handleOutcome: (status: "SUCCESS" | "FAILURE") => void
}

/**
 * Owns the reset-balance task lifecycle: resuming a persisted task on
 * mount/class-change, polling it, detecting a confirmed-gone task (404) or a
 * task stuck past the staleness TTL, and clearing state on any terminal
 * outcome. See PRD "Amendment: localStorage persistence + staleness guard".
 */
export const useResetBalanceTask = ({
  classId,
  onOutcome,
  onPollError,
}: IUseResetBalanceTaskArgs): IUseResetBalanceTaskResult => {
  const dispatch = useAppDispatch()
  const taskId = useAppSelector(selectResetBalanceTaskId)
  const taskData = useAppSelector(selectResetBalanceTaskData)

  const onOutcomeRef = useRef(onOutcome)
  onOutcomeRef.current = onOutcome
  const onPollErrorRef = useRef(onPollError)
  onPollErrorRef.current = onPollError

  const clearTask = () => {
    dispatch(setResetBalanceTaskId(null))
    dispatch(setResetBalanceTaskData(null))
    if (classId) clearPersistedResetBalanceTaskId(classId)
  }

  const applyOutcome = (outcome: IResetBalanceTaskOutcome) => {
    if (outcome.clearTask) clearTask()
    onOutcomeRef.current(outcome)
  }

  // Resume tracking an in-flight reset for the active class (e.g. after a
  // page refresh, or navigating back to this class), clear a task that
  // outlived the staleness TTL, or reset to nothing for a class with no
  // reset in flight.
  useEffect(() => {
    if (!classId) {
      dispatch(setResetBalanceTaskId(null))
      dispatch(setResetBalanceTaskData(null))
      return
    }

    if (isPersistedResetBalanceTaskStale(classId)) {
      applyOutcome(getResetBalanceStaleOutcome())
      return
    }

    dispatch(setResetBalanceTaskId(getPersistedResetBalanceTaskId(classId)))
    dispatch(setResetBalanceTaskData(null))
  }, [classId, dispatch])

  // Clear Redux (not the persisted localStorage entry - that's what lets a
  // later remount resume) when the component driving this hook unmounts, so
  // navigating away never leaves a stale task_id sitting in Redux.
  useEffect(() => {
    return () => {
      dispatch(setResetBalanceTaskId(null))
      dispatch(setResetBalanceTaskData(null))
    }
  }, [dispatch])

  useQueryAsyncTask({
    task_id: taskId,
    options: {
      refetchIntervalInBackground: true,
      refetchInterval: ASYNC_TASK_POLL_INTERVAL_MS,
      enabled: !!taskId,
      // No client-side retry: refetchInterval already re-polls every 5s, so
      // a failed poll should surface immediately via onError rather than
      // retrying with react-query's own backoff on top of that.
      retry: false,
      onSuccess: (response) => {
        const status = response.data.status
        if (
          isResetBalanceTaskInFlight(status) &&
          classId &&
          isPersistedResetBalanceTaskStale(classId)
        ) {
          applyOutcome(getResetBalanceStaleOutcome())
          return
        }
        dispatch(setResetBalanceTaskData(response.data))
      },
      onError: (err: AxiosError) => {
        if (err.response?.status === 404) {
          applyOutcome(getResetBalanceStaleOutcome())
          return
        }
        if (classId && isPersistedResetBalanceTaskStale(classId)) {
          applyOutcome(getResetBalanceStaleOutcome())
          return
        }
        onPollErrorRef.current(err)
      },
    },
  })

  const handleOutcome = (status: "SUCCESS" | "FAILURE") => {
    applyOutcome(getResetBalanceTaskOutcome(status))
  }

  return {
    isProcessingBalance: !!taskId,
    status: taskData?.status,
    handleOutcome,
  }
}
